/**
 * ENVIRONMENT: PRODUCTION
 * MODULE: Consumer Account & KYC Profile Editor (B2C)
 * PATH: src/routes/users/UserUpdateForm.js
 * COMPLIANCE: DPDP Act 2023, RBI Customer Master Standards
 */

import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import {
  Modal,
  Typography,
  Form,
  Input,
  Row,
  Col,
  Select,
  Divider,
  Tag,
  message,
} from 'antd';
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  SafetyCertificateOutlined,
  HomeOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { updateUser } from 'requests/user';

const { Text } = Typography;
const { Option } = Select;

const UserUpdateForm = (props) => {
  const { record, visible, onClose, onSubmit } = props;
  const [loading, setLoading] = useState(false);
  const [formRef] = Form.useForm();

  useEffect(() => {
    if (visible && record) {
const defaultValues = {
        fullname: record.fullname || '',
        mobile: record.mobile || '',
        email: record.email || '',
        new_password: '', // optional override
        role: record.role?.id || record.role || 'user',
        pan_number: record.pan_number || '',
        pan_name: record.pan_name || '',
        aadhar_number: record.aadhar_number || '',
        aadhar_name: record.aadhar_name || '',
        aadhar_dob: record.aadhar_dob || '',
        aadhar_gender: record.aadhar_gender || '',
        aadhar_fatherName: record.aadhar_fatherName || '',
        
        // --- HIGHLIGHTED CHANGES FOR RESIDENTIAL ADDRESS FALLBACKS ---
        address: record.address || record.street || '',
        city: record.city || record.town || '',
        state: record.state || record.province || '',
        pin_code: record.pin_code || record.pincode || record.postal_code || record.zip || '',
        // -------------------------------------------------------------
        
        verify_status: Number(record.verify_status) || 0,
        kyc_status: record.kyc_status || 'pending',
      };

      formRef.setFieldsValue(defaultValues);
    }
  }, [visible, record, formRef]);

  const onSubmitData = () => {
    formRef.validateFields().then(async (data) => {
      try {
        setLoading(true);
        const formData = new FormData();
        formData.append('fullname', data.fullname);
        formData.append('mobile', data.mobile);
        formData.append('email', data.email);
        formData.append('role', data.role || 'user');

        if (data.new_password && data.new_password.trim() !== '') {
          formData.append('simple_password', data.new_password);
        }

        // KYC & Identity
        formData.append('pan_number', data.pan_number || '');
        formData.append('pan_name', data.pan_name || '');
        formData.append('aadhar_number', data.aadhar_number || '');
        formData.append('aadhar_name', data.aadhar_name || '');
        formData.append('aadhar_dob', data.aadhar_dob || '');
        formData.append('aadhar_gender', data.aadhar_gender || '');
        formData.append('aadhar_fatherName', data.aadhar_fatherName || '');
        formData.append('verify_status', data.verify_status ?? 0);
        formData.append('kyc_status', data.verify_status === 1 ? 'verified' : 'pending');

        // Residential Address
        formData.append('address', data.address || '');
        formData.append('city', data.city || '');
        formData.append('state', data.state || '');
        formData.append('pin_code', data.pin_code || '');

        const response = await updateUser(record._id || record.id, formData);

        if (response.status === '1' || response.status === 1) {
          message.success(response.message || 'Consumer profile updated successfully.');
          onCancel();
          if (onSubmit) onSubmit();
        } else {
          message.error(response.message || 'Failed to update user profile.');
        }
      } catch (err) {
        message.error(err?.response?.data?.message || 'Error updating consumer profile.');
      } finally {
        setLoading(false);
      }
    });
  };

  const onCancel = () => {
    formRef.resetFields();
    onClose();
  };

  const handleNumericOnly = (e) => {
    const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'];
    if (!/^[0-9]$/.test(e.key) && !allowedKeys.includes(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700 }}>
          <UserOutlined style={{ color: '#0284c7' }} />
          <span>Edit Consumer Profile</span>
        </div>
      }
      open={visible}
      closable={true}
      onCancel={onCancel}
      onOk={onSubmitData}
      okButtonProps={{ loading }}
      okText="Save Changes"
      width={720}
      className="enterprise-modal-editor"
    >
      <Form layout="vertical" form={formRef}>
        {/* Section 1: Account & Contact */}
        <div style={{ marginBottom: 12 }}>
          <Text strong style={{ color: '#0f172a', fontSize: 13, textTransform: 'uppercase' }}>
            <UserOutlined style={{ marginRight: 6, color: '#0284c7' }} />
            Account &amp; Contact Credentials
          </Text>
        </div>

        <Row gutter={[16, 12]}>
          <Col span={12}>
            <Form.Item
              name="fullname"
              label="Full Name"
              rules={[{ required: true, message: 'Full Name is required' }]}
            >
              <Input placeholder="e.g. Rahul Sharma" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="mobile"
              label="Mobile Number"
              rules={[
                { required: true, message: 'Mobile number is required' },
                { len: 10, message: 'Must be a 10-digit mobile number' },
              ]}
            >
              <Input
                addonBefore="+91"
                onKeyDown={handleNumericOnly}
                maxLength={10}
                prefix={<PhoneOutlined style={{ color: '#94a3b8' }} />}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="email"
              label="Email Address"
              rules={[
                { required: true, message: 'Email address is required' },
                { type: 'email', message: 'Enter a valid email address' },
              ]}
            >
              <Input prefix={<MailOutlined style={{ color: '#94a3b8' }} />} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="new_password"
              label="Change Password"
              extra="Leave blank to retain current security credentials"
            >
              <Input.Password
                placeholder="Enter new password"
                prefix={<LockOutlined style={{ color: '#94a3b8' }} />}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ margin: '14px 0' }} />

        {/* Section 2: Identity & KYC Verification */}
        <div style={{ marginBottom: 12 }}>
          <Text strong style={{ color: '#0f172a', fontSize: 13, textTransform: 'uppercase' }}>
            <SafetyCertificateOutlined style={{ marginRight: 6, color: '#059669' }} />
            Identity &amp; e-KYC Compliance
          </Text>
        </div>

        <Row gutter={[16, 12]}>
          <Col span={12}>
            <Form.Item
              name="pan_number"
              label="Permanent Account Number (PAN)"
            >
              <Input
                placeholder="ABCDE1234F or Masked"
                maxLength={10}
                style={{ textTransform: 'uppercase' }}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name="pan_name" label="Name on PAN Record">
              <Input placeholder="As per NSDL / ITD" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="aadhar_number"
              label="Aadhaar ID Reference"
            >
              <Input
                maxLength={12}
                onKeyDown={handleNumericOnly}
                placeholder="12-digit Aadhaar"
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name="aadhar_name" label="Name on UIDAI Record">
              <Input placeholder="UIDAI verified name" />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item name="aadhar_dob" label="Date of Birth">
              <Input placeholder="DD-MM-YYYY" />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="aadhar_gender"
              label="Gender"
              getValueProps={(val) => ({
                value: val === 'M' || val === 'Male' ? 'Male' : val === 'F' || val === 'Female' ? 'Female' : val,
              })}
            >
              <Select placeholder="Select Gender">
                <Option value="Male">Male</Option>
                <Option value="Female">Female</Option>
                <Option value="Other">Other</Option>
              </Select>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item name="verify_status" label="KYC Compliance State" required>
              <Select>
                <Option value={1}>
                  <Tag color="success">Verified</Tag>
                </Option>
                <Option value={0}>
                  <Tag color="warning">Pending Verification</Tag>
                </Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ margin: '14px 0' }} />

        {/* Section 3: Residential Address */}
        <div style={{ marginBottom: 12 }}>
          <Text strong style={{ color: '#0f172a', fontSize: 13, textTransform: 'uppercase' }}>
            <HomeOutlined style={{ marginRight: 6, color: '#4f46e5' }} />
            Residential Address
          </Text>
        </div>

        <Row gutter={[16, 12]}>
          <Col span={24}>
            <Form.Item name="address" label="Street / Flat Address">
              <Input placeholder="House/Flat No, Street Name, Area" />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item name="city" label="City">
              <Input placeholder="City" />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item name="state" label="State">
              <Input placeholder="State" />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item name="pin_code" label="Postal PIN Code">
              <Input
                maxLength={6}
                onKeyDown={handleNumericOnly}
                placeholder="6-digit PIN"
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

UserUpdateForm.propTypes = {
  record: PropTypes.object.isRequired,
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
};

export default UserUpdateForm;